import React, { useState } from 'react';
import { Button, Card, Col, Form as FormAnt, message, Radio, Row, Select } from 'antd';
import { DatePicker, Space } from 'antd';
import { useParams } from 'react-router-dom';
import { useVehicleSearch } from '../../../../../../../../Hooks/TT_ELD/Logs';
import { companies } from '../../../../../../../../API/TT_ELD/Users/company';
import dayjs, { Dayjs } from 'dayjs';
import { US_STATES } from '../../../../../../../../Utils/data';
import { useProjectData } from '../../../../../../../../Hooks/useProjectData';

const { RangePicker } = DatePicker;

interface Params {
  [key: string]: string | undefined;

  id: string | undefined;
}

const IftaGenerateDays = () => {
  const [form] = FormAnt.useForm();
  const { id } = useParams<Params>();
  const { projectData } = useProjectData();
  const vehicleData: any = useVehicleSearch('', id);

  const [loadings, setLoadings] = useState<boolean[]>([]);
  const enterLoading = (index: number) => {
    setLoadings((prevLoadings) => {
      const newLoadings = [...prevLoadings];
      newLoadings[index] = true;
      return newLoadings;
    });
  };

  const stopLoading = (index: number) => {
    setLoadings((prevLoadings) => {
      const newLoadings = [...prevLoadings];
      newLoadings[index] = false;
      return newLoadings;
    });
  };
  const [startDate, setStartDate] = useState<Dayjs | null>(null);

  const disabledDate = (current: Dayjs) => {
    const today = dayjs().startOf('day'); // bugungi sana, vaqt qismi nolga tenglangan

    // Hozirgi sana (bugun) dan keyingi kunlarni bloklash
    if (current.isAfter(today, 'day')) return true;

    if (!startDate) {
      return false;
    }

    if (current.isBefore(startDate, 'day')) return true;

    if (current.diff(startDate, 'day') >= 10) return true;

    return false;
  };

  const onCalendarChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0]) {
      setStartDate(dates[0]);
    } else {
      setStartDate(null);
    }
  };

  const onSubmit = async (value: any) => {
    if (!value.date || value.date.length !== 2) {
      message.error('Please select date range');
      return;
    }

    enterLoading(1);

    const isVehicleSelected = value.vehiclesIds && value.vehiclesIds.length > 0;

    const isStatesSelected = value.states && value.states.length > 0;

    try {
      message.loading({ content: 'Processing...', key: 'regenerate' });

      const [fromDate, toDate] = value.date;

      const from_date = fromDate
        .startOf('day') // kun boshiga olib boramiz, vaqt 00:00:00
        .hour(11) // soatni 11 ga o'rnatamiz
        .minute(0)
        .second(0)
        .millisecond(0)
        .utcOffset(0, true) // UTC offsetni 0 ga o'rnatamiz, true bilan vaqtni o'zgartirmasdan
        .toISOString();

      const to_date = toDate
        .endOf('day') // kun oxiriga olib boramiz, vaqt 23:59:59.999
        .hour(11) // soatni 11 ga o'rnatamiz (endOf('day') soatini 11 ga almashtiramiz)
        .minute(0)
        .second(0)
        .millisecond(0)
        .utcOffset(0, true)
        .toISOString();

      // =========================
      // VEHICLES
      // =========================
      let vehicleUids: string[] | undefined;

      if (isVehicleSelected) {
        vehicleUids = value.vehiclesIds.length > 0 ? value.vehiclesIds : [];
      }

      // =========================
      // BODY
      // =========================
      const body: any = {
        from_date,
        to_date,
        companyId: Number(id),
        type: value.fileType, // CSV | PDF
        states: isStatesSelected ? value.states : [],
        vehicleUids: isVehicleSelected ? vehicleUids : [],
      };

      // Vehicles faqat tanlangan bo‘lsa
      if (isVehicleSelected) {
        body.vehicleUids = vehicleUids;
      }

      // States
      if (isStatesSelected) {
        body.states = value.states;
        body.all_states = false;
      } else {
        body.all_states = false;
      }

      const response = await companies.sendIftaReport(projectData.baseUrl, body);

      const file = response.file;
      const fileName =
        response.fileName ||
        `ifta_report_${from_date.substring(0, 10)}_${to_date.substring(
          0,
          10,
        )}.${value.fileType.toLowerCase()}`;

      if (typeof file === 'string') {
        message.error(file);
        return;
      }

      // Faylni yuklab olish
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({
        content: 'Report downloaded successfully!',
        key: 'regenerate',
      });
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error?.message || err?.message || 'Something went wrong!';

      message.error({
        content: errorMessage,
        key: 'regenerate',
      });

      console.error('Regenerate Error:', err);
    } finally {
      stopLoading(1);
    }
  };

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <FormAnt
          form={form}
          layout="vertical"
          name="form_in_modal"
          initialValues={{ modifier: 'public' }}
          onFinish={onSubmit}
        >
          <FormAnt.Item
            name="fileType"
            label="File Type"
            rules={[{ required: true, message: 'Please select file type!' }]}
            initialValue="csv"
          >
            <Radio.Group>
              <Radio value="csv">CSV</Radio>
              <Radio value="pdf">PDF</Radio>
            </Radio.Group>
          </FormAnt.Item>

          <Row gutter={[16, 10]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <FormAnt.Item name="states" label="States">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="All selected by default"
                  options={US_STATES.map((option: any, index) => ({
                    key: option.id || index,
                    value: option.value,
                    label: option.text,
                  }))}
                />
              </FormAnt.Item>
            </Col>
          </Row>

          <Row gutter={[16, 10]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <FormAnt.Item
                name="vehiclesIds"
                label="Vehicles"
                // rules={[{ required: true, message: 'Please enter your vehicles!' }]}
              >
                <Select
                  allowClear
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="All selected by default"
                  onChange={(vals) => {
                    if (vals.includes('all')) {
                      form.setFieldsValue({ vehiclesIds: ['all'] });
                    } else {
                      form.setFieldsValue({ vehiclesIds: vals });
                    }
                  }}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={[
                    ...(vehicleData?.data?.data?.map((option: any) => ({
                      value: option.uid,
                      label: option.truck_number,
                    })) ?? []),
                  ]}
                />
              </FormAnt.Item>
            </Col>
          </Row>

          <Row gutter={[16, 10]}>
            <Col xs={24} sm={24} md={12} lg={8}>
              <FormAnt.Item
                name="date"
                label="Select Date Range"
                rules={[{ required: true, message: 'Please select your date!' }]}
              >
                <RangePicker disabledDate={disabledDate} onCalendarChange={onCalendarChange} />
              </FormAnt.Item>
            </Col>
          </Row>

          <FormAnt.Item>
            <Button
              onClick={() => enterLoading(1)}
              loading={loadings[1]}
              type="primary"
              htmlType="submit"
            >
              Submit
            </Button>
          </FormAnt.Item>
        </FormAnt>
      </Space>
    </Card>
  );
};

export default IftaGenerateDays;
